<script setup>
import hompageBanner from "@images/homepage-banner.png";
import iphoneContentTop from "@images/iphone-content-top.png";
import image1 from "@images/image-1.png";
import phone2 from "@images/phone-2.png";
import nft from "@images/nft.png";
import mech from "@images/mech.png";
import iconSwords from "@images/svg/icon-swords.svg";
import iconSocial from "@images/svg/icon-social.svg";
import iconLeaderboards from "@images/svg/icon-leaderboards.svg";
import iconParticipation from "@images/svg/icon-participation.svg";
import iconAnalytics from "@images/svg/icon-analytics.svg";
import { carousel1, carousel2 } from "@/utils/helpers";
import { emitter } from "@plugins/mitt";

import { createParser } from "eventsource-parser";

definePage({
  name: "Homepage",
  meta: {
    layout: "default",
    public: true,
    title: "NEXUS: alpha",
    description:
      "NEXUS is a space mech 3D battle game and social platform. Join battles, squads, and earn rewards.",
  },
});

const authStore = useAuthStore();
const router = useRouter();

const totalImages = 35;
const numImagesToShow = 8;

const availableImages = Object.values(carousel2).map((img) => img.default);

const shuffledImages = [...availableImages].sort(() => Math.random() - 0.5);

const randomImages = ref(shuffledImages.slice(0, numImagesToShow));
const roomsList = ref([]);
const listOnAir = computed(() =>
  roomsList.value.filter((room) => room.isBroadcasting === true),
);
const scrollContainer = ref(null);
const speedFactor = 0.7;
const maxMomentum = 30;

let momentum = 0;
let animationFrame;
let controller;

const isFullyLoggedIn = computed(
  () => authStore.isConnectedPN && authStore.isLoggedIn,
);

onMounted(async () => {
  if (controller) {
    controller?.abort();
  }
  controller = new AbortController();
  await listenToRandomRoom();
});

onUnmounted(() => {
  controller?.abort();
});

const listenToRandomRoom = async () => {
  try {
    if (authStore.tokenAlmostExpired()) {
      await refreshToken();
    }

    const res = await sseRequest(
      `${import.meta.env.VITE_API_V1_URL}/room/all_list/stream`,
      {
        signal: controller.signal,
      },
    );

    const parser = createParser({
      onEvent(event) {
        if (event.event === "message") {
          try {
            const data = JSON.parse(event.data);
            roomsList.value = data;
          } catch (error) {
            console.error("Error parsing random room message:", error);
          }
        }
      },
      onError(error) {
        console.error("SSE parser error:", error);
      },
    });

    const reader = res.body?.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value));
    }
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("✅ SSE aborted intentionally");
    } else {
      console.error("❌ Error listening to random room:", error);
    }
  }
};

const openRoomDialog = (room) => {
  emitter.emit("openHubDialog", room);
};

const applyMomentum = () => {
  if (!scrollContainer.value) return;
  if (Math.abs(momentum) > 0.5) {
    scrollContainer.value.scrollLeft -= momentum;
    momentum *= 0.85;
    animationFrame = requestAnimationFrame(applyMomentum);
  } else {
    cancelAnimationFrame(animationFrame);
  }
};

const onDrag = ({ delta }) => {
  if (scrollContainer.value) {
    const adjustedDelta = delta[0] * speedFactor;
    scrollContainer.value.scrollLeft -= adjustedDelta;
    momentum = Math.max(-maxMomentum, Math.min(maxMomentum, adjustedDelta));
  }
};

const onDragEnd = () => {
  requestAnimationFrame(applyMomentum);
};

const handleClickJoin = () => {
  if (isFullyLoggedIn.value) {
    router.push("/base");
  } else {
    authStore.openConnectDialog();
  }
};

const vAnimateOnView = {
  mounted(el, binding) {
    const animationClass = binding.value || "tw-animate-fade-up";
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!prefersReducedMotion) {
              el.classList.add(animationClass);
            }
            el.classList.remove("tw-opacity-0");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    el.classList.add("tw-opacity-0");
    observer.observe(el);
    el._animateObserver = observer;
  },
  unmounted(el) {
    el._animateObserver?.disconnect();
  },
};
</script>

<template>
  <StarrySky class="" />
  <div class="home tw-page-container tw-text-white tw-relative" id="page-home">
    <section
      id="about"
      class="home__about tw-bg-content tw-bg-no-repeat tw-bg-top tw-py-6 md:tw-py-10 lg:tw-py-24 tw-relative"
    >
      <div
        class="tw-absolute tw-inset-0 tw-flex tw-items-end tw-justify-start tw-pr-[20%] pointer-events-none"
      >
        <v-img :src="mech" class="tw-h-[85%] tw-w-auto mech-fly-in" />
      </div>

      <v-container
        class="tw-h-[70vh] tw-max-h-[550px] tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-center"
      >
        <div
          class="tw-mb-12 tw-z-[1]"
          v-animate-on-view="'tw-animate-fade-down'"
        >
          <h2 class="tw-text-[40px] tw-mb-5">Each fall brings rebirth.</h2>
          <h4 class="tw-text-2xl">May your reign be long.</h4>
        </div>

        <div>
          <div class="text-center">
            <v-btn
              color="tw-bg-primary/50"
              width="304"
              height="54"
              class="tw-font-bold !tw-bg-primary/50 tw-rounded-xl tw-border tw-border-solid tw-border-primary tw-text-xl tw-max-w-full btn-wrap-text join-btn"
              :ripple="false"
              @click="handleClickJoin"
              v-animate-on-view="'tw-animate-scale-in'"
            >
              JOIN THE BATTLE
            </v-btn>
          </div>
        </div>
      </v-container>

      <v-container class="">
        <!-- <v-sheet class="tw-text-left" color="transparent">
          <h3 class="tw-text-[40px]">Trending live arenas...</h3>
          <div
            class="tw-w-full tw-overflow-x-auto"
            ref="scrollContainer"
            v-drag="onDrag"
            v-drag:end="onDragEnd"
            v-if="listOnAir && listOnAir.length"
          >
            <div
              class="tw-grid tw-grid-flow-col tw-auto-cols-max tw-gap-4 tw-pb-2 tw-cursor-grab tw-select-none"
            >
              <room
                v-for="(item, index) in listOnAir"
                :key="`${index}-${Date().toString()}`"
                :room="item"
                @handleShowLivestream="openRoomDialog"
              />

              <hub-dialog ref="roomDialogRef" />
            </div>
          </div>
          <div v-else>
            <div class="tw-text-white">No live arenas at the moment</div>
          </div>
        </v-sheet> -->
      </v-container>
      <!-- </div> -->
    </section>

    <section id="content" class="">
      <v-sheet color="rgba(30, 28, 28, 0.6)" class="!tw-rounded-t-3xl">
        <v-container>
          <v-sheet class="tw-text-left tw-mb-8" color="transparent">
            <h3
              class="tw-text-[40px]"
              v-animate-on-view="'tw-animate-fade-down'"
            >
              Live Now!
            </h3>
            <div
              class="tw-w-full tw-overflow-x-auto"
              ref="scrollContainer"
              v-drag="onDrag"
              v-drag:end="onDragEnd"
              v-if="listOnAir && listOnAir.length"
            >
              <div
                class="tw-grid tw-grid-flow-col tw-auto-cols-max tw-gap-4 tw-pb-2 tw-cursor-grab tw-select-none"
              >
                <room
                  v-for="(item, index) in listOnAir"
                  :key="`${index}-${Date().toString()}`"
                  :room="item"
                  @handleShowLivestream="openRoomDialog"
                />
              </div>
            </div>
            <div v-else>
              <div class="tw-text-white">No live battles at the moment</div>
            </div>
          </v-sheet>
          <!-- <v-img
            max-width="80%"
            :src="iphoneContentTop"
            class="tw-mx-auto -tw-mt-[50px] md:-tw-mt-[60px] lg:-tw-mt-[120px]"
            contain
          /> -->

          <div
            class="tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-2 tw-items-center"
          >
            <div
              class="tw-col-span-1"
              v-animate-on-view="'tw-animate-slide-left'"
            >
              <h2 class="tw-text-[40px] tw-mb-5">
                Reign victorious<br class="tw-hidden md:tw-block" />
                across the stars.
              </h2>
              <div class="tw-mb-5">
                <strong>Jack-in to your terminal</strong>, pilot your Mech and
                engage in endless Battle against other commanders - fueled by
                your war chest of coins.
              </div>
              <p class="tw-mb-5">
                Enter <strong>fully on-chain</strong> galactic battle fields,
                and bring your name honor.
              </p>
              <p class="tw-mb-5">
                Command your hoshigun (mech star-army) in
                <strong>Cataclysm</strong> and gain control of other rival
                crypto token charged Tenshu (cosmic strongholds) from other
                commanders before The Fall (game round end).
              </p>
              <p class="tw-mb-5">
                Your crypto token holdings are the <strong>fuel</strong> to
                empower your armies to move. Win the battle(control all the
                TENSHU) and receive all coins spent by all battle’s commanders
                during that battle - market bought into your faction's meme
                coin.
              </p>
              <p class="tw-mb-5">
                OR battle for Glory in the arena of
                <strong> VoidBlitz </strong>
                (mech multiplayer arcade games) and <strong>Earn</strong> a
                chunk of the reward pools for the coin of the battle season...
              </p>
              <p class="tw-mb-5">
                Download in the app stores, or play on the web.
              </p>
            </div>

            <div
              class="tw-col-span-1"
              v-animate-on-view="'tw-animate-slide-right'"
            >
              <v-img
                :src="image1"
                class="tw-mx-auto md:-tw-mr-[15px] lg:-tw-mr-[35px]"
                contain
                max-width="100%"
                eager
              />
            </div>

            <div
              class="tw-col-span-1"
              v-animate-on-view="'tw-animate-slide-left'"
            >
              <v-img max-width="80%" :src="nft" class="mx-auto" contain eager />
            </div>

            <div
              class="tw-col-span-1 md:tw-order-last"
              v-animate-on-view="'tw-animate-slide-right'"
            >
              <h2 class="tw-text-[40px] tw-mb-5">Our Mystic Rift Walkers</h2>
              <div class="tw-text-xl tw-mb-5">
                Pilot your Mech with a Vrumbler!
              </div>
              <p class="tw-mb-5">
                Our first Anointed collection is 1000 Vrumblers. They are
                cosmically-transformed generative art digitial collectibles that
                unlock NEXUS's in game characters, play to earn mechanics,
                discount the hoshigun (star army) troop movement token spend...
              </p>
              <p class="tw-mb-5">
                With this initial genesis set of Vrumblers, you can Stake the
                Collectible, earn our on-chain soulbound erc20 tokens called
                $SUJI, trade for $NEXUS, and then Anoint more Collectibles,
                Crystalize/Upgrade your Mystic powers, and more.
              </p>
            </div>
          </div>

          <v-sheet color="surface" class="!tw-rounded-[80px] tw-text-center">
            <v-container class="!tw-p-12 lg:!tw-p-[60px]">
              <h2
                class="tw-text-[40px] tw-mb-5"
                v-animate-on-view="'tw-animate-fade-down'"
              >
                NEXUS Features
              </h2>
              <div class="tw-mb-10">Explore what the app has to offer</div>

              <div
                class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-10 tw-w-full"
              >
                <div
                  class="tw-col-span-1"
                  v-animate-on-view="'tw-animate-fade-up'"
                >
                  <v-img
                    :src="iconSwords"
                    width="100"
                    class="tw-mx-auto tw-mb-4"
                  />

                  <h3 class="tw-mb-4">
                    Battle for the Cosmos<br />
                    gain control for your Coin Faction.
                  </h3>

                  <p>
                    <u>Enter ON-CHAIN real-time strategy battles</u>
                    and move your armies using coins as fuel. Win the battles.
                    Win the War.<br />Fill your Battle Chest.
                  </p>
                </div>

                <div
                  class="tw-col-span-1"
                  v-animate-on-view="'tw-animate-fade-up'"
                >
                  <v-img
                    :src="iconSocial"
                    width="100"
                    class="tw-mx-auto tw-mb-4"
                  />

                  <h3 class="tw-mb-4">
                    A Social Platform for<br />
                    Crypto Mech Battle Communities
                  </h3>

                  <p>
                    Video chat and live stream with other users. Engage with
                    your holders and tip them from your victories!
                  </p>
                </div>

                <div
                  class="tw-col-span-1"
                  v-animate-on-view="'tw-animate-fade-up'"
                >
                  <v-img
                    :src="iconLeaderboards"
                    width="100"
                    class="tw-mx-auto tw-mb-4"
                  />

                  <h3 class="tw-mb-4">
                    Climb the leaderboards and earn $NEXUS
                  </h3>

                  <p>
                    Play games and be rewarded with our dump resistent ERC20
                    battle token as well as other coins during daily/weekly
                    tourneys. Use it to upgrade your mystics, stake for token
                    launches and more.
                  </p>
                </div>

                <div
                  class="tw-col-span-1 md:tw-col-span-3 tw-flex tw-flex-wrap tw-justify-center tw-gap-10"
                >
                  <div
                    class="tw-w-full md:tw-w-1/3"
                    v-animate-on-view="'tw-animate-fade-up'"
                  >
                    <v-img
                      :src="iconParticipation"
                      width="100"
                      class="tw-mx-auto tw-mb-4"
                    />

                    <h3 class="tw-mb-4">
                      Built for crowd-wide<br />
                      participation
                    </h3>

                    <p>
                      Play against many players, Equip your Mystic Pilot, Mech
                      and Orb NFTS and gain battle advantage.
                    </p>
                  </div>

                  <div
                    class="tw-w-full md:tw-w-1/3"
                    v-animate-on-view="'tw-animate-fade-up'"
                  >
                    <v-img
                      :src="iconAnalytics"
                      width="100"
                      class="tw-mx-auto tw-mb-4"
                    />

                    <h3 class="tw-mb-4">
                      Powerful tools to measure <br />& increase engagement
                    </h3>

                    <p class="tw-mb-4">
                      As a host, engagement tools are available fo your to look
                      at the efficacy of your broadcasts.
                    </p>
                  </div>
                </div>
              </div>
            </v-container>
          </v-sheet>

          <v-sheet class="tw-py-6 md:tw-pt-10 lg:tw-pt-24" color="transparent">
            <div
              class="tw-w-[630px] tw-max-w-full tw-mx-auto tw-text-center tw-mb-10"
              v-animate-on-view="'tw-animate-fade-up'"
            >
              <h2 class="tw-text-[40px] tw-mb-5">Start battling today</h2>

              <p class="">
                With NEXUS, will you bring honor to your coin's faction? Will
                you forge a new path into the cosmos? Explore and Control
                Sectors all intune with the return of the Fall? Be the Champion
                in VoidBlitz. Reign Supreme as Commander through the Cataclysm.
                NEXUS is the gateway to glory.
              </p>
              <p class="tw-font-bold tw-underline tw-italic">
                Read through our white paper for more info.
              </p>
            </div>

            <div
              class="tw-flex tw-flex-wrap tw-flex-col tw-justify-center tw-items-center"
            >
              <div class="tw-font-bold tw-tracking-[5.2px] tw-mb-4">
                On Mobile??
              </div>
              <v-btn
                color="secondary"
                height="54"
                width="300"
                flat
                class="tw-font-bold tw-rounded-xl download-btn"
                :ripple="false"
                href="https://vrumble.app.link/CUKSUM7zVZb"
                v-animate-on-view="'tw-animate-scale-in'"
              >
                DOWNLOAD APP
              </v-btn>

              <!-- <v-btn
                color="primary"
                height="54"
                width="400"
                flat
                class="tw-font-bold tw-rounded-xl"
                to="/anoint"
              >
                SPONSOR COLLECTION
              </v-btn> -->
            </div>
          </v-sheet>
        </v-container>

        <v-sheet color="transparent" class="tw-pt-0">
          <image-carousel
            :items="randomImages"
            :hasNavitaion="false"
            :hasPagination="false"
            class=""
          />
        </v-sheet>
      </v-sheet>
    </section>
  </div>
</template>

<style scoped>
.mech-fly-in {
  opacity: 0;
  animation: mech-fly-in 1.1s ease-out 0.15s forwards;
  transform-origin: center;
}

.join-btn {
  transition:
    box-shadow 200ms ease,
    transform 200ms ease;
  box-shadow: 0 0 10px 2px rgba(var(--v-theme-primary), 0.55);
}

.join-btn:hover {
  box-shadow: 0 0 14px 3px rgba(var(--v-theme-primary), 0.85);
  transform: translateY(-1px);
}

.download-btn {
  transition:
    box-shadow 200ms ease,
    transform 200ms ease;
  box-shadow: 0 0 10px 2px rgba(var(--v-theme-secondary), 0.55);
}

.download-btn:hover {
  box-shadow: 0 0 14px 3px rgba(var(--v-theme-secondary), 0.85);
  transform: translateY(-1px);
}

@keyframes mech-fly-in {
  0% {
    opacity: 0;
    transform: translate3d(-35vw, 4vh, 0) scale(0.94);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mech-fly-in {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
</style>

<style></style>
